/**
 * MobileSidebar renders in two modes:
 * -default (mounts/unmounts components as needed)
 * -alwaysRender (always renders components, hides them by CSS on mobile devices)
 *
 * For performance reasons, wherever possible, you should stick to the default render mode.
 */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CSSTransitionGroup } from 'react-transition-group';

import { config } from 'api';
import MobileOverlay from 'utils/MobileOverlay';

export default class MobileSidebar extends PureComponent {
  static propTypes = {
    alwaysRender: PropTypes.bool,
    children: PropTypes.node,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    position: PropTypes.oneOf(['left', 'right']),
  };

  static getDerivedStateFromProps(nextProps) {
    if (!nextProps.isOpen && !nextProps.alwaysRender) {
      return {
        isFullyOpen: false,
      };
    }

    return null;
  }

  state = {
    isFullyOpen: this.props.isOpen,
  };

  static defaultProps = {
    position: 'left',
  };

  componentDidMount() {
    this.largeMediaQuery = window.matchMedia(`(min-width: ${config.breakpoints.xlarge})`);
    this.largeMediaQuery.addListener(this.onMediaQueryChanged);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen && !this.props.alwaysRender) {
      setTimeout(
        () => { this.setState({ isFullyOpen: true }); },
        this.animationDuration,
      );
    }
  }

  componentWillUnmount() {
    this.largeMediaQuery.removeListener(this.onMediaQueryChanged);
  }

  animationDuration = 300;

  onMediaQueryChanged = (mediaQueryList) => {
    if (this.props.isOpen && mediaQueryList.matches) {
      this.props.onClose();
    }
  };

  renderOverlay() {
    const { isOpen, onClose } = this.props;

    /** NOTE: this is temporary workaround for sticky desktop sidebar - @see react-stickynode */
    const wrapperNode = !config.isServer && typeof window.document !== 'undefined' &&
      window.document.querySelector('.sticky-inner-wrapper');
    const { style } = wrapperNode || {};

    if (!isOpen) {
      if (this.zIndex && wrapperNode) {
        wrapperNode.style.zIndex = this.zIndex;
      }
      return null;
    }

    if (style && wrapperNode) {
      this.zIndex = style.zIndex;
      wrapperNode.style.zIndex = '';
    }

    return (
      <MobileOverlay open={isOpen} onClose={onClose} />
    );
  }

  renderContent() {
    const { children, isOpen, alwaysRender } = this.props;
    const { isFullyOpen } = this.state;

    if (!isOpen && !alwaysRender) {
      return null;
    }

    /**
     * Classes applied for animation:
     *
     * Default mode:
     * when closed:
     *   (no content rendered)
     * when opening:
     *   -... (default class)
     *   -...-enter (added on animation start, via CSSTransitionGroup)
     *   -...-enter-active (added split second later to trigger animation - slide in, via CSSTransitionGroup)
     * when opened:
     *   -...-expanded (to clear CSS transforms to avoid bugs on iPhone/iPad)
     * when closing:
     *   -...-leave (added on animation start, via CSSTransitionGroup)
     *   -...-leave-active (added split second later to trigger animation - slide out, via CSSTransitionGroup)
     *
     * alwaysRender mode:
     * when closed:
     *   -... (default class; sidebar content hidden by CSS beyond screen borders)
     * when opening:
     *   -... (default class)
     *   -...-enter (added on animation start)
     *   -...-enter-active (added split second later to trigger animation - slide in)
     * when opened:
     *   -...-expanded (to clear CSS transforms to avoid bugs on iPhone/iPad)
     * when closing:
     *   -... (default class; enough to trigger slide out animation)
     */
    return (
      <div
        className={classNames(
          'gn-mobileSidebar__content',
          alwaysRender
            ? {
              'gn-mobileSidebar__content-enter': true,
              'gn-mobileSidebar__content-enter-active': isOpen,
              'gn-mobileSidebar__content-expanded': isOpen,
            }
            : {
              'gn-mobileSidebar__content-expanded': isFullyOpen,
            }
        )}
      >
        {children}
      </div>
    );
  }

  renderDynamicContent() {
    const { alwaysRender } = this.props;

    // If alwaysRender flag is on, we're not unmounting the component after closing,
    // so we don't need CSSTransitionGroup to delay the unmounting.
    if (alwaysRender) {
      return this.renderContent();
    }

    return (
      <CSSTransitionGroup
        transitionName="gn-mobileSidebar__content"
        transitionEnterTimeout={this.animationDuration}
        transitionLeaveTimeout={this.animationDuration}
      >
        {this.renderContent()}
      </CSSTransitionGroup>
    );
  }

  render() {
    const { alwaysRender, position } = this.props;

    return (
      <div
        className={classNames(
          'gn-mobileSidebar',
          `gn-mobileSidebar--${position}`,
          { 'gn-mobileSidebar--alwaysRender': alwaysRender },
        )}
      >
        {this.renderOverlay()}
        {this.renderDynamicContent()}
      </div>
    );
  }
}
